// ─────────────────────────────────────────────────────────────────────────────
// El CUERPO ÚNICO de los dos modales legales: el del registro (`RegisterPolicyModal`) y el
// bloqueante del primer acceso (`PolicyModal` de `LegalGate`).
//
// 🔴 Antes eran dos implementaciones de la misma cosa, y YA HABÍAN DIVERGIDO: el del registro tenía
// visor de PDF, portal, tamaño grande y aviso de error; el bloqueante no tenía ninguna de las cuatro
// y pintaba sólo texto. Duplicar otra vez garantiza que el siguiente arreglo se haga en uno y se
// olvide en el otro, así que aquí vive el cuerpo y allí sólo quedan las dos diferencias reales,
// como props:
//
//   · `dismissible` — el del registro se cierra (lo abre un enlace, es opcional); el del gate NO,
//     porque cerrarlo sería saltarse la aceptación.
//   · `versionLabel` — el del gate enseña la insignia de versión, porque es una RE-aceptación y la
//     persona necesita ver qué versión se le está pidiendo.
//
// Las dos trampas de LinkDocReg viven aquí, y son las que más fácil se rompen:
//
//   1. Al visor se le pasa `scrollClassName="policy-modal-pdf"`, una clase SIN `overflow`. El visor
//      trae su propio scroller por omisión (`.pdf-canvas-scroll`) y `.policy-modal-body` también lo
//      es: anidados, el `onScroll` de la puerta no recibe nada —el scroll ocurre dentro— y el atajo
//      de «cabe sin scroll» mide el contenedor de fuera, que ya no desborda. Se podría aceptar sin
//      haber leído nada, que es justo lo que la puerta existe para impedir.
//   2. Ese atajo espera a `onLoaded`: mientras el PDF se pinta el contenedor mide poco, y medir ahí
//      es exactamente el mismo agujero.
//
// Las dos tienen prueba en `legalDocsPdfEnRegistro.test.tsx` y en `legalGatePdfYOu.test.tsx`.
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLegalDocument } from '@/api/recognition';
import type { LegalDocType } from '@/api/recognition';
import { PdfCanvasViewer } from '@/components/common/PdfCanvasViewer';

export interface LegalPolicyModalProps {
  /** Documento a mostrar: 'privacy' (Aviso de Privacidad) | 'legal' (Bases Legales). */
  documentKey: LegalDocType;
  /**
   * OU que decide qué PDF corporativo alcanza a esta persona. Vacío = el servidor resuelve SIN
   * filtrar por OU, que es lo correcto en el registro (el enlace se abre antes de elegir
   * organización) y lo que hay que evitar cuando sí se conoce: hoy, sin OU, las Bases Legales
   * resuelven al documento de EUROPA para cualquiera.
   */
  ouId?: string;
  /**
   * Se invoca al aceptar, con la evidencia de lo que se mostró (`pdf-…`, o `null` si fue el texto).
   * Quien lo recibe decide qué hacer: el registro lo acumula en el alta, el gate lo manda al POST.
   */
  onAccept: (displayedVersion: string | null) => void;
  /** El del registro se cierra; el bloqueante NO (ver cabecera). */
  dismissible?: boolean;
  onClose?: () => void;
  /** Insignia de versión — sólo el gate la muestra. */
  versionLabel?: string;
  /** Aceptación en curso (el gate hace un POST; el registro no). */
  busy?: boolean;
  /** Error de la aceptación, ya traducido por quien lo recibió. */
  acceptError?: string | null;
}

/** Cuánto falta para el final para dar el scroll por completado (px). */
const SCROLL_TOLERANCIA = 40;

export function LegalPolicyModal({
  documentKey, ouId, onAccept, dismissible = false, onClose, versionLabel, busy = false, acceptError,
}: Readonly<LegalPolicyModalProps>) {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language?.split('-')[0] ?? 'es';
  const doc = useLegalDocument(documentKey, lang, ouId);
  const [canAccept, setCanAccept] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [pdfLoaded, setPdfLoaded] = useState(false);
  // Cada «Reintentar» fuerza el remontaje del visor: la SAS nueva casi siempre cambia la URL, pero
  // si coincidiera, sin esto el efecto atado a `url` no volvería a dispararse.
  const [retryCount, setRetryCount] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_TOLERANCIA) setCanAccept(true);
  }, []);

  const fileUrl = doc.data?.fileUrl;
  // 🔴 `contentSource` lo decide el SERVIDOR: no se deduce de `fileUrl != null`.
  const showPdf = doc.data?.contentSource === 'pdf' && Boolean(fileUrl) && !pdfError;

  const retryPdf = () => {
    setPdfError(false);
    setPdfLoaded(false);
    setRetryCount((n) => n + 1);
    // Vuelve a pedir el documento para que el backend emita una SAS fresca (la anterior pudo caducar).
    void doc.refetch();
  };

  // Documentos cortos (o el placeholder) caben sin scroll y nunca disparan `scroll`: habilitar el
  // botón cuando el contenido ya está completamente visible.
  useEffect(() => {
    if (doc.isLoading) return;
    // Con PDF hay que esperar a que el visor acabe de pintar (trampa 2 de la cabecera).
    if (showPdf && !pdfLoaded) return;
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollHeight - el.clientHeight < SCROLL_TOLERANCIA) setCanAccept(true);
  }, [doc.isLoading, doc.data, showPdf, pdfLoaded]);

  const title = doc.data?.title ?? t(`legal.${documentKey}`);
  const showPlaceholder = !doc.isLoading && !doc.data;

  // La evidencia de lo que se mostró: el `pdf-…` sólo si lo que se pintó fue el PDF. Si se degradó al
  // texto (o el visor falló), lo mostrado ES la versión comparable y no hay nada distinto que
  // declarar — `null`, no una copia.
  const displayedVersion = showPdf ? (doc.data?.version ?? null) : null;

  const acceptLabel = () => {
    if (busy) return t('admin.saving');
    return canAccept
      ? t('legal.accept', 'Acepto')
      : t('legal.scrollHint', 'Desplázate hasta el final para aceptar');
  };

  // 🔴 `createPortal` a `document.body`. Sin él el modal NUNCA puede ser más ancho que la tarjeta del
  // formulario: `.acc-card` lleva `animation: acc-card-in … both`, cuyo último fotograma deja un
  // `transform` (identidad, pero transform), y por especificación CSS eso convierte al ancestro en el
  // "containing block" de cualquier descendiente `position: fixed`. Medido: `.modal-overlay` (`inset:
  // 0`) quedaba de 426 px dentro de la tarjeta en una ventana de 1440. El del gate sustituye a los
  // hijos de `AppLayout` y hoy no tiene ese ancestro, pero se porta igual a propósito: es el mismo
  // cuerpo, y depender de que ningún ancestro futuro lleve `transform` no es una garantía.
  return createPortal((
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={dismissible ? onClose : undefined}
    >
      {/* Con PDF el modal crece (`--pdf`); con texto se queda igual. Si el visor falla, `showPdf` pasa
          a false y vuelve al tamaño de texto, que es lo que se está mostrando entonces. */}
      <div
        className={`policy-modal${showPdf ? ' policy-modal--pdf' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="policy-modal-header">
          <span className="policy-modal-title">{title}</span>
          {versionLabel && <span className="policy-modal-version">{versionLabel}</span>}
          {dismissible && (
            <button
              type="button"
              className="policy-modal-close"
              onClick={onClose}
              aria-label={t('common.close', 'Cerrar')}
            >
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          )}
        </div>

        <div className="policy-modal-body" ref={scrollRef} onScroll={onScroll}>
          {doc.isLoading && <p className="policy-section-body">{t('common.loading', 'Cargando…')}</p>}

          {pdfError && (
            <div className="policy-modal-pdf-error" role="alert">
              <p className="policy-section-body">{t('legal.pdfError', 'No se pudo mostrar el documento en PDF.')}</p>
              <div className="policy-modal-pdf-actions">
                <button type="button" className="policy-modal-pdf-link" onClick={retryPdf}>
                  {t('legal.retry', 'Reintentar')}
                </button>
                {fileUrl && (
                  <a className="policy-modal-pdf-link" href={fileUrl} target="_blank" rel="noreferrer">
                    {t('legal.openInNewTab', 'Abrir en pestaña nueva')}
                  </a>
                )}
              </div>
            </div>
          )}

          {showPdf && fileUrl && (
            <PdfCanvasViewer
              key={`${fileUrl}#${retryCount}`}
              url={fileUrl}
              onError={() => setPdfError(true)}
              onLoaded={() => setPdfLoaded(true)}
              scrollClassName="policy-modal-pdf"
              loadingClassName="policy-section-body"
            />
          )}

          {/* El texto es el respaldo cuando no hay PDF, y también cuando el visor falla: el backend
              devuelve las secciones en los dos casos. */}
          {!showPdf && doc.data?.sections.map((s) => (
            <div key={s.heading}>
              <div className="policy-section-heading">{s.heading}</div>
              <p className="policy-section-body">{s.body}</p>
            </div>
          ))}
          {showPlaceholder && (
            <p className="policy-section-body">
              {t('register.policyPlaceholder', 'Lee el documento completo. Al finalizar podrás aceptarlo.')}
            </p>
          )}
          {!doc.isLoading && (
            <div className="policy-end-marker">
              {canAccept
                ? <>✅ {t('legal.reachedEnd', '¡Has llegado al final!')}</>
                : <><i className="fas fa-chevron-down" aria-hidden="true" /> {t('legal.scrollHint', 'Desplázate hasta el final para aceptar')}</>}
            </div>
          )}
        </div>

        {acceptError && <p className="rp-error" role="alert">{acceptError}</p>}

        <div className="policy-modal-footer">
          <button
            type="button"
            className={`policy-accept-btn ${canAccept ? 'policy-accept-btn--ready' : ''}`}
            disabled={!canAccept || busy}
            onClick={() => {
              onAccept(displayedVersion);
              if (dismissible) onClose?.();
            }}
          >
            {acceptLabel()}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}

export default LegalPolicyModal;
